import React, { useEffect, useMemo, useState } from "react"
import Chart from "react-apexcharts"
import { Modal, ModalBody, ModalHeader } from "reactstrap"

const fmtGBP = val => {
  const n = Number(val)
  const abs = Math.abs(n)
  const prefix = n < 0 ? "-£" : "£"
  if (abs >= 1000000) return `${prefix}${(abs / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `${prefix}${Math.round(abs / 1000)}k`
  return `${prefix}${Math.round(abs)}`
}

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "default", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "5px 12px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "115px",
      }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "16px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "8px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.1 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const GrossMarginCustomerChart = ({ data = [] }) => {
  const [modal, setModal] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [modalData, setModalData] = useState([])
  const [sortOrder, setSortOrder] = useState("default")

  const [modalSortKey, setModalSortKey] = useState("default")
  const [modalSortOrder, setModalSortOrder] = useState("desc")

  useEffect(() => {
    setSortOrder("default")
  }, [data])

  const handleToggle = () => {
    setSortOrder(prev =>
      prev === "default"
        ? "ascending"
        : prev === "ascending"
          ? "descending"
          : "default"
    )
  }

  const handleModalHeaderSort = key => {
    if (modalSortKey === key) {
      setModalSortOrder(prev => (prev === "asc" ? "desc" : "asc"))
    } else {
      setModalSortKey(key)
      setModalSortOrder(key === "productName" ? "asc" : "desc")
    }
  }

  const handleModalSortToggle = () => {
    if (modalSortKey === "default") {
      setModalSortKey("grossMarginContribution")
      setModalSortOrder("asc")
    } else if (modalSortKey === "grossMarginContribution" && modalSortOrder === "asc") {
      setModalSortKey("grossMarginContribution")
      setModalSortOrder("desc")
    } else {
      setModalSortKey("default")
      setModalSortOrder("desc")
    }
  }

  const modalSortStateKey = useMemo(() => {
    if (modalSortKey === "default") return "default"
    if (modalSortKey === "grossMarginContribution" && modalSortOrder === "asc")
      return "ascending"
    if (modalSortKey === "grossMarginContribution" && modalSortOrder === "desc")
      return "descending"
    return "default"
  }, [modalSortKey, modalSortOrder])

  const sortedModalData = useMemo(() => {
    if (!modalData || !Array.isArray(modalData)) return []
    if (!modalSortKey || modalSortKey === "default") return modalData

    return [...modalData].sort((a, b) => {
      let valA = a[modalSortKey]
      let valB = b[modalSortKey]

      if (
        modalSortKey === "grossMarginPercent" ||
        modalSortKey === "grossMarginContribution"
      ) {
        valA = Number(valA) || 0
        valB = Number(valB) || 0
        return modalSortOrder === "asc" ? valA - valB : valB - valA
      } else {
        valA = String(valA || "").toLowerCase()
        valB = String(valB || "").toLowerCase()
        return modalSortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
    })
  }, [modalData, modalSortKey, modalSortOrder])

  const top5Positive5Negative = useMemo(() => {
    if (!data || !Array.isArray(data)) return []

    const top5 = data
      .filter(c => Number(c.totalGrossMarginContribution) >= 0)
      .sort(
        (a, b) =>
          Number(b.totalGrossMarginContribution) -
          Number(a.totalGrossMarginContribution)
      )
      .slice(0, 5)

    const bottom5 = data
      .filter(c => Number(c.totalGrossMarginContribution) < 0)
      .sort(
        (a, b) =>
          Number(a.totalGrossMarginContribution) -
          Number(b.totalGrossMarginContribution)
      )
      .slice(0, 5)

    return [...top5, ...bottom5]
  }, [data])

  const customers = useMemo(() => {
    const scoped = top5Positive5Negative

    if (sortOrder === "ascending") {
      return [...scoped].sort(
        (a, b) =>
          (Number(a.totalGrossMarginContribution) || 0) -
          (Number(b.totalGrossMarginContribution) || 0)
      )
    }

    if (sortOrder === "descending") {
      return [...scoped].sort(
        (a, b) =>
          (Number(b.totalGrossMarginContribution) || 0) -
          (Number(a.totalGrossMarginContribution) || 0)
      )
    }

    const positives = scoped
      .filter(c => Number(c.totalGrossMarginContribution) >= 0)
      .sort(
        (a, b) =>
          Number(b.totalGrossMarginContribution) -
          Number(a.totalGrossMarginContribution)
      )
    const negatives = scoped
      .filter(c => Number(c.totalGrossMarginContribution) < 0)
      .sort(
        (a, b) =>
          Number(a.totalGrossMarginContribution) -
          Number(b.totalGrossMarginContribution)
      )
    return [...positives, ...negatives]
  }, [top5Positive5Negative, sortOrder])

  const handleBarClick = (_event, _ctx, config) => {
    const idx = config.dataPointIndex
    if (idx < 0 || idx === undefined) return
    const cust = customers[idx]
    if (!cust) return
    setSelectedCustomer(cust)
    setModalData(cust.data || [])
    setModalSortKey("default")
    setModal(true)
  }

  const chartHeight = Math.max(420, customers.length * 52)

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        background: "transparent",
        events: { dataPointSelection: handleBarClick },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%",
          distributed: true,
        },
      },
      colors: customers.map(c =>
        Number(c.totalGrossMarginContribution) >= 0 ? "#22C55E" : "#EF5350"
      ),
      dataLabels: {
        enabled: true,
        textAnchor: "end",
        formatter: val => fmtGBP(val),
        style: { fontSize: "11px", fontWeight: 700, colors: ["#1a1a2e"] },
        offsetX: 40,
        background: {
          enabled: true,
          foreColor: "#fff",
          padding: 4,
          borderRadius: 4,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false },
        },
      },
      xaxis: {
        categories: customers.map(i => i.customerName),
        labels: {
          formatter: val => fmtGBP(Number(val)),
          style: { fontSize: "11px", colors: "#64748b" },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px", fontWeight: 600, colors: "#1e293b" },
        },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            strokeDashArray: 4,
            borderColor: "#333",
            borderWidth: 2,
            label: {
              borderColor: "#333",
              style: {
                color: "#333",
                background: "#fff",
                fontSize: "10px",
                fontWeight: 700,
              },
              text: "0",
              position: "top",
            },
          },
        ],
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      legend: { show: false },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const cust = customers[dataPointIndex]
          if (!cust) return ""
          const color =
            Number(cust.totalGrossMarginContribution) >= 0 ? "#22C55E" : "#EF5350"
          return `<div style="padding:10px 14px;background:#1e293b;border-radius:8px;min-width:220px;color:#f1f5f9;font-size:12px">
            <div style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Customer</div>
            <div style="font-weight:600;margin-bottom:4px">${cust.customerName
            }</div>
            <div style="font-weight:800;color:${color};font-size:14px">${fmtGBP(
              cust.totalGrossMarginContribution
            )}</div>
          </div>`
        },
      },
    }),
    [customers]
  )

  const series = useMemo(
    () => [
      {
        name: "Gross Margin Value",
        data: customers.map(c => Math.round(Number(c.totalGrossMarginContribution))),
      },
    ],
    [customers]
  )

  const positiveTotal = customers
    .filter(c => Number(c.totalGrossMarginContribution) > 0)
    .reduce((s, c) => s + Number(c.totalGrossMarginContribution), 0)

  const negativeTotal = customers
    .filter(c => Number(c.totalGrossMarginContribution) < 0)
    .reduce((s, c) => s + Number(c.totalGrossMarginContribution), 0)

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "20px 20px 16px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "15px",
                fontWeight: 700,
                margin: 0,
                color: "#1a1a2e",
              }}
            >
              Gross Margin by Customer
            </h4>
          </div>

          {/* Summary badges & Sort */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <SortButton sortOrder={sortOrder} onToggle={handleToggle} />
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "10px",
                padding: "6px 14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#15803d",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Positive
              </span>
              <span
                style={{ fontSize: "14px", fontWeight: 800, color: "#15803d" }}
              >
                {fmtGBP(positiveTotal)}
              </span>
            </div>
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "10px",
                padding: "6px 14px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#be123c",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Negative
              </span>
              <span
                style={{ fontSize: "14px", fontWeight: 800, color: "#be123c" }}
              >
                {fmtGBP(negativeTotal)}
              </span>
            </div>
          </div>
        </div>

        <Chart
          options={options}
          series={series}
          type="bar"
          height={chartHeight}
        />

        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            fontWeight: 700,
            color: "#374151",
            marginTop: "4px",
          }}
        >
          Gross Margin Value (£)
        </div>
      </div>

      {/* Drill-down Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="xl" centered>
        <ModalHeader
          toggle={() => setModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "16px",
              paddingRight: "24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
                {selectedCustomer?.customerName} Product Breakdown
              </span>
              {selectedCustomer && (
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 800,
                    color:
                      selectedCustomer.totalGrossMarginContribution >= 0
                        ? "#15803d"
                        : "#be123c",
                    background:
                      selectedCustomer.totalGrossMarginContribution >= 0
                        ? "#f0fdf4"
                        : "#fff1f2",
                    border: `1px solid ${selectedCustomer.totalGrossMarginContribution >= 0
                        ? "#bbf7d0"
                        : "#fecdd3"
                      }`,
                    borderRadius: "8px",
                    padding: "2px 10px",
                  }}
                >
                  {fmtGBP(selectedCustomer.totalGrossMarginContribution)}
                </span>
              )}
            </div>
            <SortButton
              sortOrder={modalSortStateKey}
              onToggle={handleModalSortToggle}
            />
          </div>
        </ModalHeader>
        <ModalBody
          style={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "60vh",
            overflow: "hidden",
          }}
        >
          {modalData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    {[
                      { label: "Product", key: "productName", align: "left" },
                      { label: "Category", key: "categoryName", align: "left" },
                      { label: "Gross Margin %", key: "grossMarginPercent", align: "right" },
                      {
                        label: "Gross Margin Value",
                        key: "grossMarginContribution",
                        align: "right",
                      },
                    ].map(h => {
                      const isActive = modalSortKey === h.key
                      return (
                        <th
                          key={h.key}
                          onClick={() => handleModalHeaderSort(h.key)}
                          title={`Click to sort by ${h.label}`}
                          style={{
                            padding: "10px 16px",
                            textAlign: h.align,
                            fontSize: "12px",
                            fontWeight: 700,
                            color: isActive ? "#1d4ed8" : "#64748b",
                            background: isActive ? "#eff6ff" : "transparent",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            userSelect: "none",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              flexDirection:
                                h.align === "right" ? "row-reverse" : "row",
                            }}
                          >
                            <span>{h.label}</span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: isActive ? "#2563eb" : "#94a3b8",
                                fontWeight: 800,
                              }}
                            >
                              {isActive
                                ? modalSortOrder === "asc"
                                  ? "↑"
                                  : "↓"
                                : "⇅"}
                            </span>
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedModalData.map((row, i) => {
                    const color =
                      Number(row.grossMarginContribution) >= 0
                        ? "#15803d"
                        : "#be123c"
                    return (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 16px",
                            color: "#1e293b",
                            fontWeight: 600,
                          }}
                        >
                          {row.productName}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            color: "#374151",
                            fontWeight: 500,
                          }}
                        >
                          {row.categoryName}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {Number(row.grossMarginPercent).toFixed(1)}%
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {fmtGBP(row.grossMarginContribution)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      borderTop: "2px solid #e2e8f0",
                      background: "#f8fafc",
                    }}
                  >
                    <td
                      colSpan={3}
                      style={{
                        padding: "10px 16px",
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#374151",
                        textAlign: "right",
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        textAlign: "right",
                        fontWeight: 800,
                        fontSize: "14px",
                        color:
                          modalData.reduce(
                            (s, r) => s + Number(r.grossMarginContribution),
                            0
                          ) >= 0
                            ? "#15803d"
                            : "#be123c",
                      }}
                    >
                      {fmtGBP(
                        modalData.reduce(
                          (s, r) => s + Number(r.grossMarginContribution),
                          0
                        )
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default GrossMarginCustomerChart
